import LZString from 'lz-string';

// Ultra-short field mappings for minimal URLs
const FIELD_MAP = {
  'investors': 'i',
  'keywords': 'k', 
  'company_name': 'c',
  'latest_headcount': 'e',
  'city': 'ct',
  'country': 'co',
  'funding_stage': 's',
  'funding_amount': 'a',
  'founded_year': 'y',
  'funding_date': 'd'
};

// Reverse mapping for decoding
const REVERSE_FIELD_MAP = Object.fromEntries(
  Object.entries(FIELD_MAP).map(([key, value]) => [value, key])
);

// Operator mapping for short URLs
const OP_MAP = {
  'contains': 'c',
  'multicontains': 'm', 
  'equals': 'e',
  'range': 'r',
  'gte': 'g',
  'lte': 'l',
  'gt': 'gt',
  'lt': 'lt'
};

// Reverse mapping for decoding
const REVERSE_OP_MAP = Object.fromEntries(
  Object.entries(OP_MAP).map(([key, value]) => [value, key])
);

/**
 * Encode DataGrid filterModel to compressed URL parameter
 * @param {Object} filterModel - MUI DataGrid filter model
 * @returns {string} Compressed URL parameter
 */
export function encodeFiltersToUrl(filterModel) {
  if (!filterModel?.items?.length) {
    return '';
  }

  try {
    // Convert filters to short format: field:operator:value
    const shortFilters = filterModel.items.map(filter => {
      const shortField = FIELD_MAP[filter.columnField] || filter.columnField;
      const shortOp = OP_MAP[filter.operatorValue] || filter.operatorValue;
      const value = filter.value || '';
      
      return `${shortField}:${shortOp}:${value}`;
    });

    // Join filters with pipe separator
    const filterString = shortFilters.join('|');
    
    // For very short filter strings, return as-is
    if (filterString.length < 50) {
      return filterString;
    }
    
    // For longer strings, compress with LZString
    return LZString.compressToEncodedURIComponent(filterString);
  } catch (error) {
    console.warn('Failed to encode filters to URL:', error);
    return '';
  }
}

/**
 * Decode URL parameter back to DataGrid filterModel
 * @param {string} urlParam - Compressed URL parameter
 * @returns {Object} MUI DataGrid filter model
 */
export function decodeFiltersFromUrl(urlParam) {
  if (!urlParam) {
    return { items: [] };
  }

  try {
    let filterString = urlParam;
    
    // Try to decompress if it looks like a compressed string
    if (urlParam.length > 50 || !urlParam.includes(':')) {
      const decompressed = LZString.decompressFromEncodedURIComponent(urlParam);
      if (decompressed) {
        filterString = decompressed;
      }
    }

    // Parse short format filters
    const filterItems = filterString.split('|').map(filterStr => {
      const [shortField, shortOp, ...valueParts] = filterStr.split(':');
      const value = valueParts.join(':'); // Handle values that contain colons
      
      const columnField = REVERSE_FIELD_MAP[shortField] || shortField;
      const operatorValue = REVERSE_OP_MAP[shortOp] || shortOp;
      
      return {
        columnField,
        operatorValue,
        value,
        id: Math.random() // DataGrid requires unique IDs
      };
    }).filter(filter => filter.columnField && filter.operatorValue);

    return { items: filterItems };
  } catch (error) {
    console.warn('Failed to decode filters from URL:', error);
    return { items: [] };
  }
}

/**
 * Check if two filter models are equivalent
 * @param {Object} model1 - First filter model
 * @param {Object} model2 - Second filter model
 * @returns {boolean} True if models are equivalent
 */
export function areFilterModelsEqual(model1, model2) {
  const items1 = model1?.items || [];
  const items2 = model2?.items || [];
  
  if (items1.length !== items2.length) {
    return false;
  }
  
  return items1.every((item1, index) => {
    const item2 = items2[index];
    return item1.columnField === item2.columnField &&
           item1.operatorValue === item2.operatorValue &&
           item1.value === item2.value;
  });
}